// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Brim — the PURE motion derivation (principle #23): raw accelerometer samples → a
// single smoothed "motion energy" number in roughly [0, 1.4] (0 = dead still, ~0.5 a
// noticeable twitch, ~1 a real jolt of the hand). This is the ONE evaluator every unit
// test drives, so the sensor path is proven without a real phone; the live sensor
// (input.ts) reads DeviceMotion and does nothing but push samples here, and the game
// never sees an acceleration vector. No sample is recorded, stored, or sent.
//
// It is an ATTACK/RELEASE envelope follower over the per-sample change in acceleration
// (the derivative cancels the constant ~9.8 of gravity, so it needs no calibration): a
// jerk makes energy leap (fast attack) and then decay over ~150ms (release). Flinch =
// energy over the current threshold during that window. A perfectly held phone streams
// tiny deltas and energy stays near zero.

export interface AccelSample {
  x: number;
  y: number;
  z: number;
}

export interface MotionOpts {
  /** Divisor mapping raw |Δaccel| (m/s²) to the 0..1 scale. Default 7. */
  scale?: number;
  /** Rise fraction toward a new peak per sample (0..1). Default 0.7 (fast). */
  attack?: number;
  /** Release time constant in ms — how fast energy decays after a jerk. Default 150. */
  releaseMs?: number;
  /** Hard clamp on the output. Default 1.6. */
  max?: number;
}

/**
 * Stateful, but every method is a pure function of the samples fed in — no clock, no
 * globals — so tests inject synthetic streams and get identical results everywhere.
 */
export class MotionMeter {
  private prev: AccelSample | null = null;
  private e = 0;
  private readonly scale: number;
  private readonly attack: number;
  private readonly releaseMs: number;
  private readonly max: number;

  constructor(opts: MotionOpts = {}) {
    this.scale = opts.scale ?? 7;
    this.attack = opts.attack ?? 0.7;
    this.releaseMs = opts.releaseMs ?? 150;
    this.max = opts.max ?? 1.6;
  }

  /** Feed one raw acceleration sample. Energy leaps toward a new peak; never lowered here. */
  push(s: AccelSample): void {
    if (this.prev) {
      const dx = s.x - this.prev.x;
      const dy = s.y - this.prev.y;
      const dz = s.z - this.prev.z;
      const inst = Math.min(this.max, Math.hypot(dx, dy, dz) / this.scale);
      if (inst > this.e) this.e += (inst - this.e) * this.attack;
    }
    this.prev = { x: s.x, y: s.y, z: s.z };
  }

  /** Advance time by `dtMs` with no new sample: energy decays toward zero (release). */
  decay(dtMs: number): void {
    if (dtMs <= 0) return;
    this.e *= Math.exp(-dtMs / this.releaseMs);
    if (this.e < 1e-4) this.e = 0;
  }

  /** Current smoothed motion energy. */
  value(): number {
    return this.e;
  }

  /** Zero the follower (a fresh orb / a recenter). */
  reset(): void {
    this.prev = null;
    this.e = 0;
  }
}
